import { AfterViewInit, Component, ElementRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ImageCroppedEvent, LoadedImage } from 'ngx-image-cropper';

@Component({
  selector: 'kl-image-cropper-dialog',
  standalone: false,
  templateUrl: './kl-image-cropper-dialog.html',
  styleUrl: './kl-image-cropper-dialog.scss',
})
export class KlImageCropperDialogComponent implements OnInit, AfterViewInit {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);

  readonly fileInputRef = viewChild<ElementRef<HTMLInputElement>>('fileInputRef');

  readonly imageBase64 = signal('');
  readonly imageChangedEvent = signal<Event | null>(null);
  readonly croppedImage = signal<string>('');
  readonly imageReady = signal(false);

  ngOnInit(): void {
    const src = String((this.config.data as { imageDataUrl?: unknown } | undefined)?.imageDataUrl ?? '').trim();
    this.imageBase64.set(src);
  }

  ngAfterViewInit(): void {
    const requestUploadOnOpen = Boolean(
      (this.config.data as { requestUploadOnOpen?: unknown } | undefined)?.requestUploadOnOpen
    );
    if (requestUploadOnOpen) {
      queueMicrotask(() => this.openFilePicker());
    }
  }

  onImageLoad(): void {
    this.imageReady.set(true);
  }

  cropperReady(): void {
    this.imageReady.set(true);
  }

  imageLoaded(_image: LoadedImage): void {
    this.imageReady.set(true);
  }

  loadImageFailed(): void {
    this.imageReady.set(false);
    this.croppedImage.set('');
  }

  imageCropped(event: ImageCroppedEvent): void {
    const next =
      event.base64 ?? (typeof event.objectUrl === 'string' ? event.objectUrl : String(event.objectUrl ?? ''));
    this.croppedImage.set(next);
  }

  applyCrop(): void {
    const result = this.croppedImage().trim();
    if (!result) {
      return;
    }
    this.dialogRef.close(result);
  }

  close(): void {
    this.dialogRef.close();
  }

  openFilePicker(): void {
    this.fileInputRef()?.nativeElement?.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file || !file.type.startsWith('image/')) {
      input.value = '';
      return;
    }
    this.imageChangedEvent.set(event);
    this.croppedImage.set('');
    this.imageReady.set(false);
  }
}
